/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';
import PageHeader from '../../components/PageHeader';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import TagSelector from '../../components/TagSelector';

export default function Customer() {
  const routeName = '/customers';
  const dataName = 'Cliente';
  const titleName = 'Clientes';

  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [formValues, setFormValues] = useState({
    notes: '',
    cpfCnpj: '',
    razaoSocial: '',
    nomeEmpresa: '',
    naturezaJuridica: '',
    situacaoCadastral: '',
    tipoLogradouro: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cep: '',
    pais: '',
    estado: '',
    cidade: '',
    telefone: '',
    type_person: '',
  });

  const [allServiceDesks, setAllServiceDesks] = useState([]);
  const [serviceDeskIds, setServiceDeskIds] = useState([]);
  const [initialServiceDeskIds, setInitialServiceDeskIds] = useState([]); // Mantido para referência, mas não usado na nova lógica
  const [loadingInitialData, setLoadingInitialData] = useState(true);

  // Estados para Relatórios Mensais com IA
  const [insightsAtivo, setInsightsAtivo] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [insightsConfig, setInsightsConfig] = useState({
    produtos: [],
    telefones: [''],
    servidor: '',
    tokenChat: '',
    tokenTelefonia: '',
  });

  // Estados para etiquetas
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const notification = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();

  const validationSchema = Yup.object().shape({
    type_person: Yup.string().required('Tipo de pessoa obrigatório.'),
    cpfCnpj: Yup.string().when('type_person', {
      is: 'juridica',
      then: Yup.string()
        .test('cnpj-length', 'CNPJ deve conter 14 dígitos.', function (value) {
          const cleanValue = value ? value.replace(/\D/g, '') : '';
          return cleanValue.length === 14;
        })
        .required('CNPJ obrigatório.'),
      otherwise: Yup.string()
        .test('cpf-length', 'CPF deve conter 11 dígitos.', function (value) {
          const cleanValue = value ? value.replace(/\D/g, '') : '';
          return cleanValue.length === 11;
        })
        .required('CPF obrigatório.'),
    }),
    nomeEmpresa: Yup.string().required('Nome da Empresa obrigatório.'),
    tags: Yup.array(), // Etiquetas são opcionais
  });

  const handleFieldChange = (fieldName, value) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Função para formatar CPF (xxx.xxx.xxx-xx)
  const formatCPF = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 11) {
      return cleanValue
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return cleanValue
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  // Função para formatar CNPJ (xx.xxx.xxx/xxxx-xx)
  const formatCNPJ = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 14) {
      return cleanValue
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    return cleanValue
      .slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  // Função para remover formatação (retorna apenas números)
  const removeFormatting = (value) => {
    return value.replace(/\D/g, '');
  };

  const handleCpfCnpjChange = async (e) => {
    const inputValue = e.target.value;
    let formattedValue = inputValue;

    // Aplica formatação baseada no tipo de pessoa
    if (formValues.type_person === 'fisica') {
      formattedValue = formatCPF(inputValue);
    } else if (formValues.type_person === 'juridica') {
      formattedValue = formatCNPJ(inputValue);
    }

    handleFieldChange('cpfCnpj', formattedValue);

    // Remove formatação para consulta na API (apenas números)
    const cleanCpfCnpj = removeFormatting(formattedValue);

    if (formValues.type_person === 'juridica' && cleanCpfCnpj.length === 14) {
      try {
        setIsLoading(true);
        const { data } = await axios.get(
          `https://publica.cnpj.ws/cnpj/${cleanCpfCnpj}`
        );
        setFormValues((prev) => ({
          ...prev,
          razaoSocial: data.razao_social || '',
          nomeEmpresa: data.nome_fantasia || data.razao_social || '',
          naturezaJuridica: data.natureza_juridica.descricao || '',
          situacaoCadastral: data.estabelecimento.situacao_cadastral || '',
          tipoLogradouro: data.estabelecimento.tipo_logradouro || '',
          logradouro: data.estabelecimento.logradouro || '',
          numero: data.estabelecimento.numero || '',
          complemento: data.estabelecimento.complemento || '',
          bairro: data.estabelecimento.bairro || '',
          cep: data.estabelecimento.cep || '',
          pais: data.estabelecimento.pais.nome || '',
          estado: data.estabelecimento.estado.nome || '',
          cidade: data.estabelecimento.cidade.nome || '',
          telefone:
            data.estabelecimento.ddd1 && data.estabelecimento.telefone1
              ? `(${data.estabelecimento.ddd1}) ${data.estabelecimento.telefone1}`
              : '',
        }));
        notification('Dados preenchidos automaticamente (CNPJ)', 'success');
      } catch (error) {
        console.error('Erro ao buscar dados do CNPJ:', error);
        setApiErrors(error);
        notification('Erro ao buscar dados do CNPJ', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const fetchAllServiceDesks = async () => {
      try {
        const response = await axios.get('/servicedesks');
        setAllServiceDesks(response.data);
      } catch (error) {
        console.error('Erro ao carregar Mesas de Serviço:', error);
        notification('Erro ao carregar Mesas de Serviço', 'error');
      }
    };

    const fetchAvailableTags = async () => {
      try {
        const response = await axios.get('/tags');
        setAvailableTags(response.data);
      } catch (error) {
        console.error('Erro ao carregar etiquetas:', error);
        notification('Erro ao carregar etiquetas', 'error');
      }
    };

    const fetchCustomerData = async () => {
      if (!id) {
        // modo "criar"
        setDataLoaded(true);
        setLoadingInitialData(false);
        return;
      }

      try {
        setLoadingInitialData(true);

        const { data: clienteData } = await axios.get(`${routeName}/${id}`);

        // Aplica formatação ao CPF/CNPJ baseado no tipo de pessoa
        let formattedCpfCnpj = clienteData.cpfCnpj || '';
        if (clienteData.type_person === 'fisica' && clienteData.cpfCnpj) {
          formattedCpfCnpj = formatCPF(clienteData.cpfCnpj);
        } else if (
          clienteData.type_person === 'juridica' &&
          clienteData.cpfCnpj
        ) {
          formattedCpfCnpj = formatCNPJ(clienteData.cpfCnpj);
        }

        setFormValues({
          notes: clienteData.notes || '',
          cpfCnpj: formattedCpfCnpj,
          razaoSocial: clienteData.razaoSocial || '',
          nomeEmpresa: clienteData.nomeEmpresa || '',
          naturezaJuridica: clienteData.naturezaJuridica || '',
          situacaoCadastral: clienteData.situacaoCadastral || '',
          tipoLogradouro: clienteData.tipoLogradouro || '',
          logradouro: clienteData.logradouro || '',
          numero: clienteData.numero || '',
          complemento: clienteData.complemento || '',
          bairro: clienteData.bairro || '',
          cep: clienteData.cep || '',
          pais: clienteData.pais || '',
          estado: clienteData.estado || '',
          cidade: clienteData.cidade || '',
          telefone: clienteData.telefone || '',
          type_person: clienteData.type_person || '',
        });

        // Carregar configuração de insights
        setInsightsAtivo(clienteData.insightsAtivo || false);
        setInsightsConfig({
          produtos: clienteData.insightsProdutos || [],
          telefones: clienteData.insightsTelefones?.length
            ? clienteData.insightsTelefones
            : [''],
          servidor: clienteData.insightsServidor || '',
          tokenChat: clienteData.insightsTokenChat || '',
          tokenTelefonia: clienteData.insightsTokenTelefonia || '',
        });

        // Este endpoint já retorna a lista de Service Desks associados
        const linkedDesks = clienteData.serviceDesks; // `clienteData` já vem com as associações
        const linkedIds = linkedDesks.map((desk) => desk.id);
        setServiceDeskIds(linkedIds);
        setInitialServiceDeskIds(linkedIds);

        // Carregar etiquetas do cliente
        if (clienteData.tags) {
          setSelectedTags(clienteData.tags);
        }

        setDataLoaded(true);
      } catch (error) {
        console.error('Erro ao buscar dados do cliente ou mesas:', error);
        setApiErrors(error);
        notification('Erro ao buscar dados do cliente ou mesas', 'error');
      } finally {
        setLoadingInitialData(false);
      }
    };

    fetchAllServiceDesks();
    fetchAvailableTags();
    fetchCustomerData();
  }, [id, notification]);

  const handleCheckboxChange = (deskId) => {
    setServiceDeskIds((prevIds) => {
      if (prevIds.includes(deskId)) {
        return prevIds.filter((x) => x !== deskId);
      }
      return [...prevIds, deskId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Etiquetas são opcionais - não há validação necessária

    setIsLoading(true);
    try {
      // 1. Crie o objeto de dados a ser enviado
      const dataToSave = {
        ...formValues,
        cpfCnpj: removeFormatting(formValues.cpfCnpj), // Remove formatação antes de enviar
        serviceDeskIds, // Envia a lista COMPLETA de IDs para o backend
        tagIds: selectedTags.map((tag) => tag.id), // Envia IDs das etiquetas selecionadas
        insightsAtivo,
        insightsProdutos: insightsAtivo ? insightsConfig.produtos : [],
        insightsTelefones: insightsAtivo
          ? insightsConfig.telefones.filter((t) => t.trim())
          : [],
        insightsServidor: insightsAtivo ? insightsConfig.servidor : '',
        insightsTokenChat: insightsAtivo ? insightsConfig.tokenChat : '',
        insightsTokenTelefonia: insightsAtivo
          ? insightsConfig.tokenTelefonia
          : '',
      };

      let saved;
      if (id) {
        const resp = await axios.put(`${routeName}/${id}`, dataToSave);
        saved = resp.data;
      } else {
        const resp = await axios.post(routeName, dataToSave);
        saved = resp.data;
      }

      notification(`${dataName} salvo com sucesso`, 'success');
      navigate(routeName);
    } catch (err) {
      console.error('Erro ao salvar cliente:', err.response || err);
      setApiErrors(err);
      notification('Falha ao salvar cliente', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsightsToggle = () => {
    if (!insightsAtivo) {
      setShowInsightsModal(true);
    }
    setInsightsAtivo((prev) => !prev);
  };

  const handleInsightsProdutoChange = (produto) => {
    setInsightsConfig((prev) => {
      const produtos = prev.produtos.includes(produto)
        ? prev.produtos.filter((p) => p !== produto)
        : [...prev.produtos, produto];
      return { ...prev, produtos };
    });
  };

  const handleTelefoneChange = (index, value) => {
    setInsightsConfig((prev) => {
      const telefones = [...prev.telefones];
      telefones[index] = value;
      return { ...prev, telefones };
    });
  };

  const addTelefone = () => {
    setInsightsConfig((prev) => ({
      ...prev,
      telefones: [...prev.telefones, ''],
    }));
  };

  const removeTelefone = (index) => {
    setInsightsConfig((prev) => ({
      ...prev,
      telefones: prev.telefones.filter((_, i) => i !== index),
    }));
  };

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      {/* Spinner global */}
      <Loading isLoading={isLoading || loadingInitialData} />

      {/* Breadcrumbs e Cabeçalho */}
      <Breadcrumbs
        title={id ? 'Atualizar' : 'Cadastrar'}
        pages={[{ link: routeName, name: titleName }]}
      />
      <PageHeader title={titleName} />

      <section>
        <Row>
          <Col lg={12}>
            <Card className="mb-5">
              <Card.Header>
                <h4 className="card-heading">
                  {id ? `Atualizar ${dataName}` : `Cadastrar ${dataName}`}
                </h4>
                <div>
                  <Alert variant="info">
                    <strong>Observações:</strong>
                    <br />
                    Para cadastrar clientes do tipo <strong>Jurídica</strong>,
                    informe o <strong>Tipo pessoa</strong> e o{' '}
                    <strong>CNPJ</strong>. Você pode colar apenas os números ou
                    com formatação.
                    <br />O sistema aplicará a formatação automaticamente.
                    Aguarde que a <strong>API da receita</strong> irá preencher
                    os demais campos. Para <strong>Pessoa física</strong>, o{' '}
                    <strong>CPF</strong> também será formatado automaticamente.
                  </Alert>
                </div>
              </Card.Header>

              <Card.Body>
                {/* Exibe erros de validação ou de API */}
                <ErrorProcessor error={apiErrors} />

                {dataLoaded && !loadingInitialData ? (
                  <form onSubmit={handleSubmit}>
                    <Row>
                      {/* ... (campos do formulário permanecem os mesmos) ... */}
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="type_person">Tipo de pessoa</label>
                          <select
                            id="type_person"
                            className="form-control"
                            value={formValues.type_person}
                            onChange={(e) =>
                              handleFieldChange('type_person', e.target.value)
                            }
                          >
                            <option value="">-- Selecione --</option>
                            <option value="fisica">Física</option>
                            <option value="juridica">Juridica</option>
                          </select>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="cpfCnpj">CPF ou CNPJ</label>
                          <input
                            type="text"
                            id="cpfCnpj"
                            className="form-control"
                            value={formValues.cpfCnpj}
                            onChange={handleCpfCnpjChange}
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="razaoSocial">Razão Social</label>
                          <input
                            type="text"
                            id="razaoSocial"
                            className="form-control"
                            value={formValues.razaoSocial}
                            onChange={(e) =>
                              handleFieldChange('razaoSocial', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="nomeEmpresa">Nome da Empresa</label>
                          <input
                            type="text"
                            id="nomeEmpresa"
                            className="form-control"
                            value={formValues.nomeEmpresa}
                            onChange={(e) =>
                              handleFieldChange('nomeEmpresa', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="naturezaJuridica">
                            Natureza Jurídica
                          </label>
                          <input
                            type="text"
                            id="naturezaJuridica"
                            className="form-control"
                            value={formValues.naturezaJuridica}
                            readOnly
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="situacaoCadastral">
                            Situação Cadastral
                          </label>
                          <input
                            type="text"
                            id="situacaoCadastral"
                            className="form-control"
                            value={formValues.situacaoCadastral}
                            readOnly
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="tipoLogradouro">
                            Tipo de Logradouro
                          </label>
                          <input
                            type="text"
                            id="tipoLogradouro"
                            className="form-control"
                            value={formValues.tipoLogradouro}
                            onChange={(e) =>
                              handleFieldChange(
                                'tipoLogradouro',
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="logradouro">Logradouro</label>
                          <input
                            type="text"
                            id="logradouro"
                            className="form-control"
                            value={formValues.logradouro}
                            onChange={(e) =>
                              handleFieldChange('logradouro', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="numero">Número</label>
                          <input
                            type="text"
                            id="numero"
                            className="form-control"
                            value={formValues.numero}
                            onChange={(e) =>
                              handleFieldChange('numero', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="complemento">Complemento</label>
                          <input
                            type="text"
                            id="complemento"
                            className="form-control"
                            value={formValues.complemento}
                            onChange={(e) =>
                              handleFieldChange('complemento', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="bairro">Bairro</label>
                          <input
                            type="text"
                            id="bairro"
                            className="form-control"
                            value={formValues.bairro}
                            onChange={(e) =>
                              handleFieldChange('bairro', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label htmlFor="cep">CEP</label>
                          <input
                            type="text"
                            id="cep"
                            className="form-control"
                            value={formValues.cep}
                            onChange={(e) =>
                              handleFieldChange('cep', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="form-group mb-3">
                          <label htmlFor="pais">País</label>
                          <input
                            type="text"
                            id="pais"
                            className="form-control"
                            value={formValues.pais}
                            onChange={(e) =>
                              handleFieldChange('pais', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="form-group mb-3">
                          <label htmlFor="estado">Estado</label>
                          <input
                            type="text"
                            id="estado"
                            className="form-control"
                            value={formValues.estado}
                            onChange={(e) =>
                              handleFieldChange('estado', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="form-group mb-3">
                          <label htmlFor="cidade">Cidade</label>
                          <input
                            type="text"
                            id="cidade"
                            className="form-control"
                            value={formValues.cidade}
                            onChange={(e) =>
                              handleFieldChange('cidade', e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="form-group mb-3">
                          <label htmlFor="telefone">Telefone</label>
                          <input
                            type="text"
                            id="telefone"
                            className="form-control"
                            value={formValues.telefone}
                            onChange={(e) =>
                              handleFieldChange('telefone', e.target.value)
                            }
                          />
                        </div>
                      </Col>

                      <Col md={12}>
                        <div className="form-group mb-3">
                          <label htmlFor="notes">Informações do cliente</label>
                          <RichTextEditor
                            value={formValues.notes || ''}
                            onChange={(newValue) =>
                              handleFieldChange('notes', newValue)
                            }
                            placeholder="Digite as informações do cliente aqui..."
                            maxImages={10}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Seção Etiquetas */}
                    <div
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        padding: '12px',
                        borderRadius: '0.25rem 0.25rem 0 0',
                        marginTop: '1.5rem',
                      }}
                    >
                      <strong>Etiquetas</strong>
                    </div>
                    <div
                      style={{
                        padding: '1rem',
                        border: '1px solid #e0e0e0',
                        borderTop: 'none',
                        borderRadius: '0 0 0.25rem 0.25rem',
                        marginBottom: '1.5rem',
                      }}
                    >
                      <TagSelector
                        availableTags={availableTags}
                        selectedTags={selectedTags}
                        onChange={setSelectedTags}
                        placeholder="Selecione pelo menos uma etiqueta..."
                        isMulti
                        showSelectedBadges
                        required
                      />
                      <small className="text-muted">
                        * Pelo menos uma etiqueta deve ser selecionada
                      </small>
                    </div>

                    {/* Seção Mesas de Serviço */}
                    <div
                      style={{
                        backgroundColor: '#3b3fbd',
                        color: 'white',
                        padding: '12px',
                        borderRadius: '0.25rem 0.25rem 0 0',
                        marginTop: '1.5rem',
                      }}
                    >
                      <strong>Mesas de Serviço</strong>
                    </div>
                    <div
                      style={{
                        padding: '1rem',
                        border: '1px solid #e0e0e0',
                        borderTop: 'none',
                        borderRadius: '0 0 0.25rem 0.25rem',
                        marginBottom: '1.5rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '0.75rem',
                        }}
                      >
                        {allServiceDesks.map((desk) => (
                          <label
                            key={desk.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              fontWeight: 500,
                              fontSize: '0.95rem',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={serviceDeskIds.includes(desk.id)}
                              onChange={() => handleCheckboxChange(desk.id)}
                              style={{ marginRight: '0.5rem' }}
                            />
                            {desk.displayName}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Seção Relatórios Mensais com IA */}
                    <div
                      style={{
                        backgroundColor: '#6f42c1',
                        color: 'white',
                        padding: '12px',
                        borderRadius: '0.25rem 0.25rem 0 0',
                        marginTop: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <strong>Relatórios Mensais com IA</strong>
                      <Form.Check
                        type="switch"
                        id="insights-switch"
                        checked={insightsAtivo}
                        onChange={handleInsightsToggle}
                        label={insightsAtivo ? 'Ativo' : 'Inativo'}
                        style={{ color: 'white', marginBottom: 0 }}
                      />
                    </div>
                    {insightsAtivo && (
                      <div
                        style={{
                          padding: '1rem',
                          border: '1px solid #e0e0e0',
                          borderTop: 'none',
                          borderRadius: '0 0 0.25rem 0.25rem',
                          marginBottom: '1.5rem',
                        }}
                      >
                        <Row>
                          <Col md={12} className="mb-3">
                            <label className="form-label fw-semibold">
                              Produtos
                            </label>
                            <div className="d-flex gap-3">
                              <Form.Check
                                type="checkbox"
                                id="produto-telefonia"
                                label="Telefonia"
                                checked={insightsConfig.produtos.includes(
                                  'telefonia'
                                )}
                                onChange={() =>
                                  handleInsightsProdutoChange('telefonia')
                                }
                              />
                              <Form.Check
                                type="checkbox"
                                id="produto-chatclinic"
                                label="ChatClinic"
                                checked={insightsConfig.produtos.includes(
                                  'chatclinic'
                                )}
                                onChange={() =>
                                  handleInsightsProdutoChange('chatclinic')
                                }
                              />
                            </div>
                          </Col>

                          <Col md={12} className="mb-3">
                            <label className="form-label fw-semibold">
                              Telefones para envio dos relatórios
                            </label>
                            {insightsConfig.telefones.map((tel, index) => (
                              <div key={index} className="d-flex gap-2 mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Ex: 11999999999"
                                  value={tel}
                                  onChange={(e) =>
                                    handleTelefoneChange(index, e.target.value)
                                  }
                                />
                                {insightsConfig.telefones.length > 1 && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeTelefone(index)}
                                  >
                                    ✕
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={addTelefone}
                            >
                              + Adicionar telefone
                            </Button>
                          </Col>

                          <Col md={12} className="mb-3">
                            <label className="form-label fw-semibold">
                              Servidor (DNS ou IP Público)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Ex: meuservidor.com.br ou 200.100.50.10"
                              value={insightsConfig.servidor}
                              onChange={(e) =>
                                setInsightsConfig((prev) => ({
                                  ...prev,
                                  servidor: e.target.value,
                                }))
                              }
                            />
                            <small className="text-muted">
                              Apenas DNS ou IP público. IPs locais não são
                              suportados.
                            </small>
                          </Col>

                          {insightsConfig.produtos.includes('chatclinic') && (
                            <Col md={6} className="mb-3">
                              <label className="form-label fw-semibold">
                                Token ChatClinic
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Token da API do ChatClinic"
                                value={insightsConfig.tokenChat}
                                onChange={(e) =>
                                  setInsightsConfig((prev) => ({
                                    ...prev,
                                    tokenChat: e.target.value,
                                  }))
                                }
                              />
                            </Col>
                          )}

                          {insightsConfig.produtos.includes('telefonia') && (
                            <Col md={6} className="mb-3">
                              <label className="form-label fw-semibold">
                                Token Telefonia
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Token da API da Telefonia"
                                value={insightsConfig.tokenTelefonia}
                                onChange={(e) =>
                                  setInsightsConfig((prev) => ({
                                    ...prev,
                                    tokenTelefonia: e.target.value,
                                  }))
                                }
                              />
                            </Col>
                          )}
                        </Row>
                      </div>
                    )}

                    {/* Botão de envio */}
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isLoading}
                    >
                      {id ? 'Atualizar' : 'Cadastrar'}
                    </Button>
                  </form>
                ) : (
                  <p>Carregando dados...</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
